<template>
  <div class="shell">
    <!-- Alert/Notification System -->
    <div v-if="alert.message" :class="['alert', alert.type]">
      <span>{{ alert.message }}</span>
      <button @click="alert.message = ''" class="alert-close">×</button>
    </div>

    <aside class="sidebar">
      <div class="brand">
        <div class="brand-icon">🔮</div>
        <div class="brand-text">
          <div class="brand-title">VectorHub</div>
          <div class="brand-subtitle">AI Vector Database</div>
        </div>
      </div>
      
      <nav class="menu">
        <button :class="{ active: activeTab==='dashboard' }" @click="activeTab='dashboard'">
          <span class="menu-icon">📊</span>
          <span class="menu-text">Dashboard</span>
        </button>
        <button :class="{ active: activeTab==='upload' }" @click="activeTab='upload'">
          <span class="menu-icon">📁</span>
          <span class="menu-text">Upload</span>
        </button>
        <button :class="{ active: activeTab==='search' }" @click="activeTab='search'">
          <span class="menu-icon">🔍</span>
          <span class="menu-text">Search</span>
        </button>
      </nav>

      <div class="sidebar-footer">
        <div class="system-status">
          <div class="status-dot" :class="healthStatus"></div>
          <span>API Status: {{ healthStatus }}</span>
        </div>
        <div v-if="healthData.collections_count" class="collection-count">
          {{ healthData.collections_count }} Collections
        </div>
      </div>
    </aside>

    <main class="main">
      <header class="topbar">
        <div class="topbar-left">
          <h1>{{ tabs.find(t=>t.id===activeTab)?.name }}</h1>
          <div v-if="activeTab === 'dashboard'" class="topbar-badge">
            {{ collections.length }} collections
          </div>
          <div v-if="activeTab === 'upload'" class="topbar-badge">
            {{ selectedFiles.length }} files selected
          </div>
        </div>
        <div class="topbar-right">
          <button @click="refreshData" class="btn-icon" title="Refresh">
            <span>🔄</span>
          </button>
        </div>
      </header>

      <section class="content">
        <!-- Dashboard -->
        <div v-if="activeTab==='dashboard'">
          <div class="dashboard-header">
            <div class="dashboard-stats">
              <div class="stat-card">
                <div class="stat-icon">📚</div>
                <div class="stat-content">
                  <div class="stat-value">{{ collections.length }}</div>
                  <div class="stat-label">Collections</div>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon">💾</div>
                <div class="stat-content">
                  <div class="stat-value">{{ totalPoints }}</div>
                  <div class="stat-label">Total Vectors</div>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon">⚡</div>
                <div class="stat-content">
                  <div class="stat-value">{{ healthStatus }}</div>
                  <div class="stat-label">API Status</div>
                </div>
              </div>
            </div>
          </div>

          <div class="panel-grid">
            <div class="panel">
              <div class="panel-header">
                <h3>Collections Management</h3>
                <button @click="showCreateCollection = !showCreateCollection" class="btn-sm">
                  {{ showCreateCollection ? 'Cancel' : '+ New Collection' }}
                </button>
              </div>
              
              <div v-if="showCreateCollection" class="create-collection-form">
                <div class="dept-naming-hint">
                  <span class="hint-icon">ℹ️</span>
                  <span>
                    Name wird gespeichert als <strong>{{ selectedDept }}_{{ newCollectionSuffix || 'mein_name' }}</strong>.
                    Nur die Abteilung <strong>{{ selectedDept.toUpperCase() }}</strong> kann diese Collection im CompanyGPT sehen.
                  </span>
                </div>
                <div class="form-grid form-grid--dept">
                  <div>
                    <label class="form-label">Abteilung</label>
                    <select v-model="selectedDept" class="input-full">
                      <option v-for="dept in knownDepartments" :key="dept" :value="dept">
                        {{ dept.toUpperCase() }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="form-label">Collection-Name</label>
                    <div class="prefix-input-row">
                      <span class="prefix-badge">{{ selectedDept }}_</span>
                      <input v-model="newCollectionSuffix" placeholder="z.B. handbuch_2024"
                             class="input-full" :class="{ error: newCollectionSuffix && !validateSuffix }" />
                    </div>
                  </div>
                  <div>
                    <label class="form-label">Vector Size</label>
                    <input v-model.number="newCollectionVectorSize" type="number"
                           placeholder="3072" class="input-full" min="1" max="4096" />
                  </div>
                  <button @click="createCollection" :disabled="!validateCollectionName || isCreatingCollection"
                          class="btn-primary">
                    {{ isCreatingCollection ? 'Creating...' : '+ Create Collection' }}
                  </button>
                </div>
                <div v-if="newCollectionSuffix && !validateSuffix" class="form-hint form-hint--error">
                  Nur Buchstaben, Zahlen und Unterstriche (3–45 Zeichen)
                </div>
                <div v-if="validateCollectionName" class="form-hint form-hint--preview">
                  ✅ Wird erstellt als: <code>{{ fullCollectionName }}</code>
                </div>
              </div>

              <div class="table-container">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Vectors</th>
                      <th>Size</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in collections" :key="c.name">
                      <td>
                        <div class="collection-name">
                          <span class="collection-icon">📦</span>
                          {{ c.name }}
                          <span v-if="c.name==='default'" class="collection-badge">Default</span>
                        </div>
                      </td>
                      <td>{{ c.points_count || 0 }}</td>
                      <td>{{ c.vector_size || 'N/A' }}</td>
                      <td>
                        <div class="action-buttons">
                          <button @click="useCollection(c.name)" class="btn-action" title="Use this collection">
                            📝
                          </button>
                          <button @click="deleteCollection(c.name)" 
                                  :disabled="c.name==='default'" 
                                  class="btn-action danger" title="Delete collection">
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload -->
        <div v-if="activeTab==='upload'" class="upload-container">
          <div class="panel-grid">
            <div class="panel">
              <div class="panel-header">
                <h3>Document Upload</h3>
                <div class="file-counter" v-if="selectedFiles.length">
                  {{ selectedFiles.length }} file(s) selected
                </div>
              </div>

              <div class="upload-zone" @dragover.prevent @drop.prevent="handleDrop">
                <input type="file" ref="fileInput" multiple @change="handleFileChange" hidden />
                <div class="upload-content" @click="$refs.fileInput.click()">
                  <div class="upload-icon">📁</div>
                  <div class="upload-text">
                    <div class="upload-title">Drop files here or click to browse</div>
                    <div class="upload-subtitle">Supports JSON, PDF, DOCX, TXT (Max 50MB each)</div>
                  </div>
                </div>
                <div v-if="selectedFiles.length" class="selected-files">
                  <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    <button @click.stop="removeFile(index)" class="file-remove">×</button>
                  </div>
                </div>
              </div>

              <div class="form-section">
                <h4>Upload Configuration</h4>
                <div class="form-grid">
                  <div class="form-group">
                    <label>Target Collection</label>
                    <div class="select-wrapper">
                      <select v-model="uploadConfig.collectionName">
                        <option disabled value="">Select Collection</option>
                        <option v-for="col in collections" :key="col.name" :value="col.name">
                          {{ col.name }} ({{ col.points_count || 0 }} vectors)
                        </option>
                      </select>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label>Chunk Size</label>
                    <input type="range" v-model.number="uploadConfig.chunkSize" min="500" max="3000" step="100" />
                    <div class="range-value">{{ uploadConfig.chunkSize }} tokens</div>
                  </div>

                  <div class="form-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="uploadConfig.flattenNested" />
                      <span>Flatten nested JSON</span>
                    </label>
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="uploadConfig.extractTables" />
                      <span>Extract tables from documents</span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="upload-actions">
                <button @click="clearSelection" :disabled="!selectedFiles.length" class="btn-secondary">
                  Clear Selection
                </button>
                <button @click="uploadFiles" :disabled="!canUpload || isUploading" class="btn-primary">
                  <span v-if="isUploading">
                    <span class="spinner"></span>
                    Uploading... {{ uploadProgress }}%
                  </span>
                  <span v-else>Start Upload</span>
                </button>
              </div>

              <div v-if="uploadResults.length" class="upload-results">
                <h4>Upload Results</h4>
                <div class="results-grid">
                  <div v-for="r in uploadResults" :key="r.filename" 
                       :class="['result-card', r.status]">
                    <div class="result-icon">
                      {{ r.status === 'success' ? '✅' : '❌' }}
                    </div>
                    <div class="result-content">
                      <div class="result-title">{{ r.filename }}</div>
                      <div class="result-details">
                        <span v-if="r.chunks_created">{{ r.chunks_created }} chunks created</span>
                        <span v-if="r.message">{{ r.message }}</span>
                        <span v-if="r.reason" class="error-text">{{ r.reason }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Search -->
        <div v-if="activeTab==='search'" class="search-container">
          <div class="panel-grid">
            <div class="panel">
              <div class="panel-header">
                <h3>Semantic Search</h3>
              </div>

              <div class="search-input-container">
                <div class="search-icon">🔍</div>
                <textarea v-model="searchConfig.query" placeholder="Enter your search query..." 
                          class="search-textarea" rows="3"></textarea>
              </div>

              <div class="form-section">
                <div class="form-grid">
                  <div class="form-group">
                    <label>Collection</label>
                    <div class="select-wrapper">
                      <select v-model="searchConfig.collectionName">
                        <option disabled value="">Select Collection</option>
                        <option v-for="c in collections" :key="c.name" :value="c.name">
                          {{ c.name }} ({{ c.points_count || 0 }} vectors)
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>Results Limit</label>
                    <input type="range" v-model.number="searchConfig.limit" min="1" max="50" />
                    <div class="range-value">{{ searchConfig.limit }} results</div>
                  </div>

                  <div class="form-group">
                    <label>Score Threshold</label>
                    <input type="range" v-model.number="searchConfig.scoreThreshold" min="0" max="1" step="0.1" />
                    <div class="range-value">{{ searchConfig.scoreThreshold.toFixed(2) }}</div>
                  </div>
                </div>
              </div>

              <div class="search-actions">
                <button @click="searchDocuments" :disabled="!canSearch || isSearching" class="btn-primary">
                  <span v-if="isSearching">
                    <span class="spinner"></span>
                    Searching...
                  </span>
                  <span v-else>Search Documents</span>
                </button>
              </div>

              <div v-if="searchExecuted" class="search-results">
                <div class="results-header">
                  <h4>Search Results</h4>
                  <div class="results-count">{{ searchResults.length }} results found</div>
                </div>
                
                <div v-if="searchResults.length" class="results-list">
                  <div v-for="(result, index) in searchResults" :key="result.id" class="result-item">
                    <div class="result-rank">#{{ index + 1 }}</div>
                    <div class="result-content">
                      <div class="result-header">
                        <div class="result-score">
                          <div class="score-badge" :style="{ 
                            background: `linear-gradient(90deg, var(--success) ${result.score * 100}%, var(--gray-200) ${result.score * 100}%)` 
                          }">
                            Score: {{ result.score.toFixed(4) }}
                          </div>
                        </div>
                        <div v-if="result.payload.source" class="result-source">
                          Source: {{ result.payload.source }}
                        </div>
                      </div>
                      <div class="result-text">{{ result.payload.text }}</div>
                      <div v-if="result.payload.metadata" class="result-metadata">
                        <span v-for="(value, key) in result.payload.metadata" :key="key" class="metadata-tag">
                          {{ key }}: {{ value }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-results">
                  <div class="no-results-icon">🤔</div>
                  <div class="no-results-text">No matching documents found</div>
                  <div class="no-results-hint">Try lowering the score threshold or using different keywords</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';

// --- CONFIGURATION ---
// Ohne VUE_APP_ Präfix
//const API_BASE_URL = process.env.VUE_APP_BACKEND_URL || 'http://localhost:8000/api';
console.log('=== DEBUG INFO ===');
console.log('process.env:', process.env);
console.log('process.env.VUE_APP_BACKEND_URL:', process.env.VUE_APP_BACKEND_URL);
console.log('window.APP_CONFIG:', window.APP_CONFIG);
console.log('window.location.href:', window.location.href);
console.log('=== END DEBUG ===');
// Dynamische URL Bestimmung
const getApiBaseUrl = () => {
  const host = window.location.hostname;

  // 1. Wenn wir auf dem neuen KI-Server sind
  if (host.includes('ki.schuberth.info')) {
    return 'https://upload-api.ki.schuberth.info/api';
  }

  // 2. Wenn wir auf dem alten/internen Server sind
  if (host.includes('intern.schuberth.info')) {
    return 'https://upload-backend.intern.schuberth.info/api';
  }

  // 3. Fallback für lokale Entwicklung oder falls obiges nicht matcht
  return '/api'; 
};

const API_BASE_URL = getApiBaseUrl();
console.log('🌐 API_BASE_URL:', API_BASE_URL);
// const API_BASE_URL = process.env.VUE_APP_BACKEND_URL || 
//                      window.APP_CONFIG?.API_BASE_URL || 
//                      'http://localhost:8000/api';

// console.log('API_BASE_URL:', API_BASE_URL);

console.log('API_BASE_URL:', API_BASE_URL); // Debugging
const activeTab = ref('dashboard');
const tabs = [
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'upload', name: 'Upload Documents' },
  { id: 'search', name: 'Semantic Search' },
];

const healthData = ref({});
const collections = ref([]);
const alert = ref({ message: '', type: '' });

// Dashboard State
const showCreateCollection = ref(false);
//const newCollectionName = ref('');       // DEPRECATED – wird nicht mehr direkt benutzt
const newCollectionSuffix = ref('');     // NEU: nur der Teil nach dem Dept-Prefix
const newCollectionVectorSize = ref(3072);
const isCreatingCollection = ref(false);

// Bekannte Departments (muss mit Backend KNOWN_DEPT_PREFIXES übereinstimmen)
const knownDepartments = [
  'it', 'hr', 'sales', 'marketing', 'finance', 'legal',
  'qm', 'rnd', 'asff', 'mille', 'motocycle', 'racing',
  'composite', 'decals', 'grinding', 'paint', 'pp', 'sp',
  'fa', 'fico', 'ie', 'im', 'maint', 'management',
  'op', 'pm', 'ps', 'receiving', 'shipping', 'sc',
];
const selectedDept = ref('it'); // Default-Auswahl

// Upload State
const selectedFiles = ref([]);
const fileInput = ref(null);
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadResults = ref([]);
const uploadConfig = ref({
  collectionName: '',
  chunkSize: 1500,
  flattenNested: true,
  extractTables: true,
});

// Search State
const searchConfig = ref({
  query: '',
  collectionName: '',
  limit: 10,
  scoreThreshold: 0.7,
});
const isSearching = ref(false);
const searchResults = ref([]);
const searchExecuted = ref(false);

// --- COMPUTED PROPERTIES ---
const healthStatus = computed(() => healthData.value.status || 'unknown');

// Validierung des Suffix (Teil nach dem Dept-Prefix)
const validateSuffix = computed(() => {
  const s = newCollectionSuffix.value;
  return s.length >= 2 && s.length <= 45 && /^[a-z0-9_]+$/.test(s);
});

// Vollständiger Collection-Name = dept_suffix
const fullCollectionName = computed(() => {
  return selectedDept.value + '_' + newCollectionSuffix.value;
});

const validateCollectionName = computed(() => {
  return validateSuffix.value;
});

const totalPoints = computed(() => {
  return collections.value.reduce((sum, col) => sum + (col.points_count || 0), 0);
});

const canUpload = computed(() => {
  return selectedFiles.value.length > 0 && 
         uploadConfig.value.collectionName && 
         !isUploading.value;
});

const canSearch = computed(() => {
  return searchConfig.value.query.trim() && 
         searchConfig.value.collectionName && 
         !isSearching.value;
});

// --- METHODS ---

function showAlert(message, type = 'info') {
  alert.value = { message, type };
  setTimeout(() => {
    alert.value = { message: '', type: '' };
  }, 5000);
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

async function refreshData() {
  await fetchHealthCheck();
  await fetchCollections();
  showAlert('Data refreshed successfully', 'success');
}

async function fetchHealthCheck() {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`);
    healthData.value = response.data;
  } catch (error) {
    console.error("Health Check Error:", error);
    healthData.value = { status: 'unhealthy', error: error.message };
  }
}

async function fetchCollections() {
  try {
    const response = await axios.get(`${API_BASE_URL}/collections`);
    collections.value = response.data;
    // Set default collections if not set
    if (collections.value.length > 0) {
      if (!uploadConfig.value.collectionName) {
        uploadConfig.value.collectionName = collections.value[0].name;
      }
      if (!searchConfig.value.collectionName) {
        searchConfig.value.collectionName = collections.value[0].name;
      }
    }
  } catch (error) {
    console.error("Collections Fetch Error:", error);
    showAlert('Error loading collections', 'error');
  }
}

async function createCollection() {
  if (!validateCollectionName.value) {
    showAlert('Ungültiger Collection-Name', 'error');
    return;
  }

  isCreatingCollection.value = true;
  const name = fullCollectionName.value; // z.B. "it_hydra_doku"

  try {
    await axios.post(`${API_BASE_URL}/collections`, {
      name: name,
      vector_size: newCollectionVectorSize.value,
    });

    showAlert(`✅ Collection "${name}" erstellt`, 'success');
    newCollectionSuffix.value = '';
    newCollectionVectorSize.value = 3072;
    showCreateCollection.value = false;
    await fetchCollections();

  } catch (error) {
    const message = error.response?.data?.detail || error.message;
    showAlert(`❌ Fehler: ${message}`, 'error');
    console.error("Create Collection Error:", error);
  } finally {
    isCreatingCollection.value = false;
  }
}
function useCollection(name) {
  uploadConfig.value.collectionName = name;
  searchConfig.value.collectionName = name;
  activeTab.value = 'upload';
  showAlert(`Now using collection: ${name}`, 'success');
}

async function deleteCollection(name) {
  if (name === 'default') {
    showAlert('Cannot delete default collection', 'error');
    return;
  }
  
  if (!confirm(`Are you sure you want to delete collection "${name}"? This action cannot be undone.`)) {
    return;
  }
  
  try {
    // ✅ ENTFERNE das Kommentar und rufe die API wirklich auf
    const response = await axios.delete(`${API_BASE_URL}/collections/${name}`);
    
    console.log("Delete response:", response.data); // Für Debugging
    
    showAlert(`Collection "${name}" deleted successfully`, 'success');
    
    // Liste aktualisieren
    await fetchCollections();
    
    // Wenn die gelöschte Collection aktuell ausgewählt war, zurücksetzen
    if (uploadConfig.value.collectionName === name) {
      uploadConfig.value.collectionName = collections.value.length > 0 ? collections.value[0].name : '';
    }
    if (searchConfig.value.collectionName === name) {
      searchConfig.value.collectionName = collections.value.length > 0 ? collections.value[0].name : '';
    }
    
  } catch (error) {
    const message = error.response?.data?.detail || error.message;
    showAlert(`Error deleting collection: ${message}`, 'error');
    console.error("Delete Collection Error:", error);
  }
}
function handleDrop(event) {
  const files = Array.from(event.dataTransfer.files);
  const validFiles = files.filter(file => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const validTypes = [
      'application/json',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    return file.size <= maxSize && validTypes.some(type => file.type.includes(type));
  });
  
  selectedFiles.value.push(...validFiles);
  
  if (validFiles.length !== files.length) {
    showAlert('Some files were skipped due to size or type restrictions', 'warning');
  }
}

function handleFileChange(event) {
  const files = Array.from(event.target.files);
  selectedFiles.value.push(...files);
}

function removeFile(index) {
  selectedFiles.value.splice(index, 1);
}

function clearSelection() {
  selectedFiles.value = [];
  if (fileInput.value) fileInput.value.value = '';
}

async function uploadFiles() {
  if (!canUpload.value) return;
  
  isUploading.value = true;
  uploadProgress.value = 0;
  uploadResults.value = [];
  
  try {
    // Process JSON files
    const jsonFiles = selectedFiles.value.filter(f => f.name.toLowerCase().endsWith('.json'));
    
    for (const [index, file] of jsonFiles.entries()) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('collection_name', uploadConfig.value.collectionName);
      formData.append('flatten_nested', uploadConfig.value.flattenNested.toString());
      
      try {
        const response = await axios.post(`${API_BASE_URL}/upload/json`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadResults.value.push({
          filename: file.name,
          status: 'success',
          chunks_created: response.data.total_items,
          message: `${response.data.total_items} items processed`
        });
      } catch (error) {
        uploadResults.value.push({
          filename: file.name,
          status: 'error',
          reason: error.response?.data?.detail || error.message
        });
      }
      
      uploadProgress.value = Math.round(((index + 1) / selectedFiles.value.length) * 50);
    }
    
    // Process other files
    const otherFiles = selectedFiles.value.filter(f => !f.name.toLowerCase().endsWith('.json'));
    
    if (otherFiles.length > 0) {
      const formData = new FormData();
      otherFiles.forEach(file => formData.append('files', file));
      formData.append('collection_name', uploadConfig.value.collectionName);
      formData.append('chunk_size', uploadConfig.value.chunkSize.toString());
      formData.append('extract_tables', uploadConfig.value.extractTables.toString());
      
      try {
        const response = await axios.post(`${API_BASE_URL}/upload/file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const fileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 50);
            uploadProgress.value = 50 + fileProgress;
          }
        });
        uploadResults.value.push(...response.data.results);
      } catch (error) {
        otherFiles.forEach(file => {
          uploadResults.value.push({
            filename: file.name,
            status: 'error',
            reason: error.response?.data?.detail || error.message
          });
        });
      }
    }
    
    // Success handling
    const successful = uploadResults.value.filter(r => r.status === 'success');
    if (successful.length > 0) {
      showAlert(`${successful.length} files uploaded successfully`, 'success');
      await fetchCollections(); // Update collection counts
    }
    
    uploadProgress.value = 100;
    
  } catch (error) {
    console.error("Upload Error:", error);
    showAlert('Upload failed: ' + (error.response?.data?.detail || error.message), 'error');
  } finally {
    isUploading.value = false;
    setTimeout(() => {
      uploadProgress.value = 0;
    }, 2000);
  }
}

async function searchDocuments() {
  if (!canSearch.value) return;
  
  isSearching.value = true;
  searchExecuted.value = false;
  searchResults.value = [];
  
  try {
    const response = await axios.post(`${API_BASE_URL}/search`, {
      query: searchConfig.value.query,
      collection_name: searchConfig.value.collectionName,
      limit: searchConfig.value.limit,
      score_threshold: searchConfig.value.scoreThreshold,
    });
    
    searchResults.value = response.data.results;
    searchExecuted.value = true;
    
    if (searchResults.value.length === 0) {
      showAlert('No results found. Try adjusting the score threshold.', 'info');
    }
    
  } catch (error) {
    const message = error.response?.data?.detail || error.message;
    showAlert(`Search error: ${message}`, 'error');
    console.error("Search Error:", error);
  } finally {
    isSearching.value = false;
  }
}

// --- WATCHERS ---
watch(activeTab, (newTab) => {
  if (newTab === 'dashboard') {
    fetchCollections();
  }
});

// --- LIFECYCLE HOOKS ---
onMounted(() => {
  fetchHealthCheck();
  fetchCollections();
  
  // Auto-refresh every 30 seconds
  setInterval(() => {
    fetchHealthCheck();
    if (activeTab.value === 'dashboard') {
      fetchCollections();
    }
  }, 30000);
});
</script>

<style>
/* Modern CSS Variables */
:root {
  --primary: #f97316;
  --primary-dark: #f97316;
  --secondary: #f97316;
  --success: #10b981;
  --warning: #f59e0b;
  --danger: #ef4444;
  --info: #3b82f6;
  
  --gray-50: #f8fafc;
  --gray-100: #f1f5f9;
  --gray-200: #e2e8f0;
  --gray-300: #cbd5e1;
  --gray-400: #94a3b8;
  --gray-500: #64748b;
  --gray-600: #475569;
  --gray-700: #334155;
  --gray-800: #1e293b;
  --gray-900: #0f172a;
  
  --sidebar-width: 280px;
  --header-height: 70px;
  --border-radius: 12px;
  --border-radius-sm: 8px;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Reset & Base Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.shell {
  display: flex;
  min-height: 100vh;
  background: var(--gray-50);
  backdrop-filter: blur(20px);
}

/* Alert System */
.alert {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius);
  background: white;
  box-shadow: var(--shadow-xl);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  z-index: 1000;
  animation: slideIn 0.3s ease;
  max-width: 400px;
  border-left: 4px solid var(--gray-400);
}

.alert.success {
  border-left-color: var(--success);
  background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%);
}

.alert.error {
  border-left-color: var(--danger);
  background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%);
}

.alert.warning {
  border-left-color: var(--warning);
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
}

.alert.info {
  border-left-color: var(--info);
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
}

.alert-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--gray-500);
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.alert-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Sidebar */
.sidebar {
  width: var(--sidebar-width);
  background: linear-gradient(180deg, var(--gray-900) 0%, var(--gray-800) 100%);
  color: white;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: var(--shadow-xl);
  z-index: 10;
}

.sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
}

.brand {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
  padding: 1rem;
  border-radius: var(--border-radius);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
}

.brand-icon {
  font-size: 2rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(90deg, #fff, var(--gray-300));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-subtitle {
  font-size: 0.875rem;
  color: var(--gray-400);
  margin-top: 0.25rem;
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.menu button {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: none;
  border-radius: var(--border-radius);
  background: transparent;
  color: var(--gray-300);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  text-align: left;
  position: relative;
  overflow: hidden;
}

.menu button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
  transform: translateX(4px);
}

.menu button.active {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  color: white;
  box-shadow: var(--shadow-md);
}

.menu button.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--primary), var(--secondary));
}

.menu-icon {
  font-size: 1.25rem;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-text {
  flex: 1;
}

.sidebar-footer {
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.system-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-400);
}

.status-dot.healthy {
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.status-dot.unhealthy {
  background: var(--danger);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  animation: pulse 2s infinite;
}

.collection-count {
  font-size: 0.875rem;
  color: var(--gray-400);
  text-align: center;
  padding: 0.5rem;
  border-radius: var(--border-radius-sm);
  background: rgba(255, 255, 255, 0.05);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Main Content */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.topbar {
  height: var(--header-height);
  background: white;
  padding: 0 2rem;
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: var(--shadow-sm);
  position: sticky;
  top: 0;
  z-index: 5;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.topbar h1 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0;
}

.topbar-badge {
  padding: 0.25rem 0.75rem;
  background: var(--gray-100);
  color: var(--gray-600);
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--gray-100);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  color: var(--gray-600);
}

.btn-icon:hover {
  background: var(--gray-200);
  transform: rotate(180deg);
}

/* Content Area */
.content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background: var(--gray-50);
}

/* Dashboard */
.dashboard-header {
  margin-bottom: 2rem;
}

.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: var(--shadow-md);
  transition: var(--transition);
  border: 1px solid transparent;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--primary);
}

.stat-icon {
  font-size: 2rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--gray-900);
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
}

/* Panels */
.panel-grid {
  display: grid;
  gap: 2rem;
}

.panel {
  background: white;
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--gray-200);
  transition: var(--transition);
}

.panel:hover {
  box-shadow: var(--shadow-lg);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--gray-100);
}

.panel-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-900);
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: var(--border-radius-sm);
  background: var(--gray-100);
  border: none;
  color: var(--gray-700);
  cursor: pointer;
  transition: var(--transition);
}

.btn-sm:hover {
  background: var(--gray-200);
}

/* Forms */
.form-grid {
  display: grid;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);
}

.input-full {
  padding: 0.75rem 1rem;
  border: 2px solid var(--gray-300);
  border-radius: var(--border-radius-sm);
  font-size: 0.95rem;
  transition: var(--transition);
  background: white;
}

.input-full:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.input-full.error {
  border-color: var(--danger);
}

.form-hint {
  font-size: 0.75rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
}

.select-wrapper {
  position: relative;
}

.select-wrapper::after {
  content: '⌄';
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--gray-500);
}

select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--gray-300);
  border-radius: var(--border-radius-sm);
  font-size: 0.95rem;
  background: white;
  appearance: none;
  cursor: pointer;
  transition: var(--transition);
}

select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid var(--gray-300);
  cursor: pointer;
}

.checkbox-label input[type="checkbox"]:checked {
  background: var(--primary);
  border-color: var(--primary);
}

/* Tables */
.table-container {
  overflow-x: auto;
  margin-top: 1.5rem;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--gray-200);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: var(--gray-50);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--gray-700);
  border-bottom: 2px solid var(--gray-200);
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
  color: var(--gray-600);
}

.data-table tr:hover {
  background: var(--gray-50);
}

.collection-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.collection-icon {
  font-size: 1.25rem;
}

.collection-badge {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  background: var(--gray-100);
  color: var(--gray-600);
  border-radius: 12px;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--gray-100);
  color: var(--gray-600);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
}

.btn-action:hover {
  background: var(--gray-200);
  transform: scale(1.1);
}

.btn-action.danger:hover {
  background: var(--danger);
  color: white;
}

/* Upload Zone */
.upload-zone {
  border: 3px dashed var(--gray-300);
  border-radius: var(--border-radius);
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 2rem;
  background: var(--gray-50);
}

.upload-zone:hover {
  border-color: var(--primary);
  background: rgba(99, 102, 241, 0.05);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  font-size: 3rem;
  color: var(--gray-400);
}

.upload-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
}

.upload-subtitle {
  font-size: 0.875rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
}

.selected-files {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--gray-200);
}

.file-name {
  font-weight: 500;
  color: var(--gray-800);
}

.file-size {
  font-size: 0.875rem;
  color: var(--gray-500);
}

.file-remove {
  background: none;
  border: none;
  color: var(--danger);
  font-size: 1.5rem;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.file-remove:hover {
  background: rgba(239, 68, 68, 0.1);
}

.form-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: var(--gray-50);
  border-radius: var(--border-radius);
}

.form-section h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 1rem;
}

/* Range Inputs */
input[type="range"] {
  width: 100%;
  height: 6px;
  background: var(--gray-300);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: var(--primary);
  border-radius: 50%;
  cursor: pointer;
}

.range-value {
  font-size: 0.875rem;
  color: var(--gray-600);
  text-align: center;
  margin-top: 0.5rem;
}

/* Buttons */
.btn-primary, .btn-secondary {
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--gray-100);
  color: var(--gray-700);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--gray-200);
}

.upload-actions, .search-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-200);
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Upload Results */
.upload-results, .search-results {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-200);
}

.results-grid {
  display: grid;
  gap: 1rem;
}

.result-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: var(--border-radius-sm);
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
}

.result-card.success {
  background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%);
  border-color: rgba(16, 185, 129, 0.2);
}

.result-card.error {
  background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%);
  border-color: rgba(239, 68, 68, 0.2);
}

.result-icon {
  font-size: 1.5rem;
}

.result-content {
  flex: 1;
}

.result-title {
  font-weight: 600;
  color: var(--gray-800);
  margin-bottom: 0.25rem;
}

.result-details {
  font-size: 0.875rem;
  color: var(--gray-600);
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.error-text {
  color: var(--danger);
}

/* Search */
.search-input-container {
  position: relative;
  margin-bottom: 2rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 1rem;
  font-size: 1.25rem;
  color: var(--gray-400);
}

.search-textarea {
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid var(--gray-300);
  border-radius: var(--border-radius);
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  background: white;
  transition: var(--transition);
}

.search-textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.results-count {
  font-size: 0.875rem;
  color: var(--gray-500);
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.result-item {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: var(--border-radius);
  border: 1px solid var(--gray-200);
  transition: var(--transition);
}

.result-item:hover {
  transform: translateX(4px);
  border-color: var(--primary);
  box-shadow: var(--shadow-md);
}

.result-rank {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--primary);
  min-width: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.result-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.result-score {
  flex: 1;
}

.score-badge {
  display: inline-block;
  padding: 0.375rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-800);
  background: var(--gray-200);
  border: 1px solid var(--gray-300);
}

.result-source {
  font-size: 0.875rem;
  color: var(--gray-600);
  padding: 0.25rem 0.75rem;
  background: var(--gray-100);
  border-radius: 4px;
}

.result-text {
  color: var(--gray-700);
  line-height: 1.6;
  max-height: 4.8em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.result-metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.metadata-tag {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  background: var(--gray-100);
  color: var(--gray-600);
  border-radius: 4px;
  border: 1px solid var(--gray-200);
}

.no-results {
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: var(--border-radius);
  border: 2px dashed var(--gray-300);
}

.no-results-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-results-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
}

.no-results-hint {
  font-size: 0.875rem;
  color: var(--gray-500);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .sidebar {
    width: 240px;
  }
}

@media (max-width: 768px) {
  .shell {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    flex-direction: row;
    padding: 1rem;
  }
  
  .brand {
    margin-bottom: 0;
    margin-right: 2rem;
  }
  
  .menu {
    flex-direction: row;
    flex: 1;
    gap: 0.25rem;
  }
  
  .menu button {
    flex-direction: column;
    padding: 0.75rem;
    font-size: 0.75rem;
  }
  
  .menu-text {
    display: none;
  }
  
  .content {
    padding: 1rem;
  }
  
  .dashboard-stats {
    grid-template-columns: 1fr;
  }
  
  .upload-actions, .search-actions {
    flex-direction: column;
  }
  
  .btn-primary, .btn-secondary {
    width: 100%;
  }
}

/* Loading States */
.loading {
  opacity: 0.7;
  pointer-events: none;
  position: relative;
}

.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  margin: -12px 0 0 -12px;
  border: 3px solid var(--gray-300);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* ── Department Collection Creator ─────────────────────────────── */
.dept-naming-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  font-size: 13px;
  color: #c7d2fe;
  line-height: 1.5;
}
.hint-icon { font-size: 15px; flex-shrink: 0; }

.form-grid--dept {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 12px;
  align-items: end;
}
.form-grid--dept > .btn-primary {
  grid-column: span 2;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-400);
  margin-bottom: 5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.prefix-input-row {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--gray-600);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
}
.prefix-badge {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  font-family: monospace;
  font-size: 13px;
  font-weight: 700;
  padding: 0 10px;
  white-space: nowrap;
  border-right: 1px solid rgba(99, 102, 241, 0.3);
  height: 38px;
  display: flex;
  align-items: center;
}
.prefix-input-row .input-full {
  border: none !important;
  border-radius: 0 !important;
  flex: 1;
}

.form-hint--error {
  color: #f87171;
  font-size: 12px;
  margin-top: 4px;
}
.form-hint--preview {
  color: #6ee7b7;
  font-size: 12px;
  margin-top: 6px;
}
.form-hint--preview code {
  background: rgba(110, 231, 183, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
  font-family: monospace;
}

</style>